import { BottomFiller, GradiantOverlay, GraphWrapper } from './ProjectionGraph.styled';
import { CategoryScale, Chart as ChartJS, Filler, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import { Line } from 'react-chartjs-2';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { current } from 'immer';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface IProps {
  height?: number;
  paddingBottom?: number;
  dataPoints: any;
  type?: number;
  dates: any;
  show?: any;
}

export const ProjectionGraph = (props: IProps) => {
  const skipped = (ctx, value) => (ctx.p1DataIndex < props.dataPoints[0].dataset.length ? value : undefined);
  const [data, setData] = useState({ dataSet1: undefined, dataSet2: undefined });
  const [redraw, setRedraw] = useState<number>(0);
  const d = useRef<number>(0);

  useEffect(() => {
    if (props.dataPoints?.length > 0) {
      getDataSets();
    }
  }, [props.dataPoints, props.show]);

  const getDataSets = () => {
    let d1 = {
      labels: props.dataPoints[1].dataset?.map((dp) => dp),
      datasets: props.dataPoints.map((dp, index) => {
        let temp = {
          backgroundColor: 'rgba(185, 212, 215, 0)',
          fill: 'start',
          tension: 0.2,
        };

        if (index === 0) {
          return Object.assign(temp, {
            data: dp.dataset,
            borderDash: [12, 6],
            borderColor: (ctx) => (ctx.dataIndex === dp.dataset.length - 1 ? 'rgba(47, 97, 68, 1)' : 'rgba(47, 97, 68, 0)'),
            backgroundColor: (ctx) => (ctx.dataIndex === dp.dataset.length - 1 ? dp.color : 'rgba(47, 97, 68, 0)'),
            pointRadius: (ctx) => {
              return ctx.dataIndex === dp.dataset.length - 1 ? 5 : 0;
            },
            label: dp.text,
            pointHitRadius: (ctx) => {
              return ctx.dataIndex === dp.dataset.length - 1 ? 5 : 0;
            },
          });
        } else if (index === 2) {
          return {
            data: dp.dataset,
            borderDash: [12, 6],
            borderColor: dp.color,
            backgroundColor: (ctx) => (ctx.dataIndex === dp.dataset.length - 1 ? dp.color : 'rgba(185, 212, 215, 0)'),
            fill: 'start',
            tension: 0.2,
            label: dp.text,
            segment: {
              borderColor: (ctx) => (props.show.bestCase ? skipped(ctx, 'rgba(0,0,0,0.0)') : 'rgba(0,0,0,0.0)'),
              borderDash: (ctx) => skipped(ctx, [6, 6]),
            },
            pointRadius: (ctx) => {
              return ctx.dataIndex === dp.dataset.length - 1 && props.show.bestCase ? 5 : 0;
            },
            pointHitRadius: (ctx) => {
              return ctx.dataIndex < props.dataPoints[0].dataset.length - 1 && props.show.bestCase ? 5 : 0;
            },
          };
        } else if (index === 1) {
          return {
            data: dp.dataset,
            borderDash: [12, 6],
            borderColor: dp.color,
            label: dp.text,
            backgroundColor: (ctx) => (ctx.dataIndex === dp.dataset.length - 1 ? dp.color : 'rgba(185, 212, 215, 0)'),
            segment: {
              borderColor: (ctx) => {
                return props.show.worstCase ? skipped(ctx, 'rgba(0,0,0,0.0)') : 'rgba(0,0,0,0.0)';
              },
              borderDash: (ctx) => skipped(ctx, [6, 6]),
            },
            pointRadius: (ctx) => {
              return ctx.dataIndex === dp.dataset.length - 1 && props.show.worstCase ? 5 : 0;
            },
            pointHitRadius: (ctx) => {
              return ctx.dataIndex === dp.dataset.length - 1 && props.show.worstCase ? 5 : 0;
            },
          };
        }
      }),
    };
    let d2 = {
      labels: props.dataPoints[1].dataset?.map((dp) => dp),
      datasets: props.dataPoints.map((dp, index) => {
        //ændrer til 2 hvis grafen skal fyldes fuldt ud
        if (index === 0) {
          return {
            data: dp.dataset,
            borderDash: [12, 6],
            borderColor: 'transparent',
            backgroundColor: 'rgba(185, 212, 215, 1)',
            fill: 'start',
            tension: 0.2,
          };
        } else {
          return { data: dp.dataset, borderDash: [12, 6], borderColor: 'transparent' };
        }
      }),
    };
    setData({
      dataSet1: d1,
      dataSet2: d2,
    });
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        callbacks: {
          label: (context: any) => {
            let label = '';
            if (context.formattedValue) {
              label = context.formattedValue + '%';
            }
            return label;
          },
          title: (context: any) => {
            let date = moment(props.dates[context[0].dataIndex]);
            if (context[0].dataIndex < props.dataPoints[0].dataset.length - 1) return date.format('DD-MM-YYYY');
            return context[0].dataset.label;
          },
        },
      },
    },
    elements: {
      line: {},
      point: {
        radius: 0,
        hitRadius: 0,
      },
    },
    scales: {
      x: {
        display: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          align: 'inner',
          minRotation: 45,
          maxRotation: 45,
          font: {
            size: 16,
            weight: '500',
            family: 'Jost',
          },
          color: ['unset', 'unset', 'red'],
          callback: function (value, index, ticks) {
            let now = moment();
            let date = moment(props.dates[index]);
            if (index === 0 || index === props.dates.length - 1 || now.isSame(date, 'date')) return date.format('DD MMM');
            else return null;
          },
        },
      },
      y: {
        border: {
          display: false,
        },
        display: true,
        beginAtZero: true,
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            weight: '500',
            family: 'Jost',
          },
          stepSize: 10,
          callback: function (value, index, ticks) {
            return value + '%';
          },
        },
      },
    },
    layout: {
      padding: 0,
    },
  };

  return data.dataSet1 === undefined || data.dataSet2 === undefined ? null : props.type === 1 ? (
    <GraphWrapper ix={1} paddingBottom={props.paddingBottom ? props.paddingBottom : 0}>
      {/* @ts-ignore*/}
      <Line data={data.dataSet1} width={100} height={300} options={options} />
    </GraphWrapper>
  ) : (
    <GraphWrapper paddingBottom={props.paddingBottom ? props.paddingBottom : 0}>
      {/* @ts-ignore*/}
      <Line data={data.dataSet2} width={100} height={300} options={options} />
      <GradiantOverlay />
    </GraphWrapper>
  );
};

export default ProjectionGraph;
